import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Flex } from 'foundations-components/components/Flex';
import { useState, useCallback } from 'react';
import { Text } from 'foundations-components/components/Text';
import { useAiGenerateViewModalState } from '../hooks/useAiGenerateViewModalState';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import AILoadingButton from 'ai-components-ui-library/components/buttons/AILoadingButton';
import { viewsManagementTracker } from '../../tracking/viewsManagementTracking';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import { getStoredAiViewData, storeAiViewData } from '../../utils/aiViewStorage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AiGenerateViewModal = ({
  objectTypeId,
  currentViewId,
  onClose,
  onCurrentViewIdChange,
  objectTypeName,
  isRegenerate = false,
  pinnedViewsBarLogger
}) => {
  const storedData = isRegenerate ? getStoredAiViewData(objectTypeId, currentViewId) : null;
  const [description, setDescription] = useState(() => {
    return String((storedData === null || storedData === void 0 ? void 0 : storedData.description) || '');
  });
  const existingViewId = String((storedData === null || storedData === void 0 ? void 0 : storedData.viewId) || '');
  const {
    handleGenerateOrUpdate,
    loadingGeneratedView,
    generatedView,
    error
  } = useAiGenerateViewModalState({
    viewDescription: description,
    objectTypeId,
    onClose,
    isRegenerate,
    existingViewId
  });
  const handleDescriptionChange = useCallback(e => setDescription(e.target.value), []);
  const trackViewGeneration = useCallback(() => {
    if (isRegenerate) {
      pinnedViewsBarLogger === null || pinnedViewsBarLogger === void 0 || pinnedViewsBarLogger.log({
        key: 'trackAiViewRegenerate',
        metadata: {
          objectTypeId,
          viewId: existingViewId || ''
        }
      });
    } else {
      pinnedViewsBarLogger === null || pinnedViewsBarLogger === void 0 || pinnedViewsBarLogger.log({
        key: 'trackAiViewGenerate',
        metadata: {
          objectTypeId
        }
      });
    }
  }, [isRegenerate, objectTypeId, existingViewId, pinnedViewsBarLogger]);
  const handleFormSubmit = useCallback(() => {
    if (loadingGeneratedView) {
      return;
    }
    trackViewGeneration();
    handleGenerateOrUpdate();
  }, [loadingGeneratedView, trackViewGeneration, handleGenerateOrUpdate]);
  const handleSubmitFormKeyPress = useCallback(e => {
    const isEnterWithoutShift = e.key === 'Enter' && !e.shiftKey;
    if (isEnterWithoutShift) {
      e.preventDefault();
      handleFormSubmit();
    }
  }, [handleFormSubmit]);
  if (generatedView) {
    var _generatedView$viewId;
    const viewId = ((_generatedView$viewId = generatedView.viewId) === null || _generatedView$viewId === void 0 ? void 0 : _generatedView$viewId.toString()) || '';
    const viewName = generatedView.name || '';
    if (viewId) {
      onCurrentViewIdChange(viewId, true, viewName);
      storeAiViewData(objectTypeId, viewId, {
        description,
        name: viewName
      });
    }
    onClose();
  }
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "ai-generate-view-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "300",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h2",
          color: "fill-surface-default",
          children: isRegenerate ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.reGenerateView"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.title"
          })
        }), /*#__PURE__*/_jsx(Badge, {
          use: "beta",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.beta"
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(Form, {
      onKeyDown: handleSubmitFormKeyPress,
      children: [/*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "body-200",
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.describeView",
            options: {
              objectTypeName: objectTypeName || 'record'
            }
          })
        }), /*#__PURE__*/_jsx(ExpandingTextArea, {
          "data-test-id": "ai-generate-view-modal-input",
          value: description,
          onChange: handleDescriptionChange,
          maxHeight: 120,
          minHeight: 120,
          autoFocus: true
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexDirection: "column",
          marginBlockStart: "300",
          children: [loadingGeneratedView && /*#__PURE__*/_jsx(UILoadingOverlay, {
            "data-test-id": "ai-generate-view-modal-loading-overlay",
            contextual: true,
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "viewsManagement.modals.generateView.generatingView"
            }),
            showLabel: true,
            layout: "centered"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "viewsManagement.modals.generateView.aiWarning"
            })
          })]
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [isRegenerate ? /*#__PURE__*/_jsx(AILoadingButton, {
          "data-test-id": "ai-generate-view-modal-re-generate-button",
          isRegenerate: true,
          usageTracker: viewsManagementTracker,
          onClick: handleFormSubmit,
          disabled: loadingGeneratedView,
          loading: loadingGeneratedView,
          children: error ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.retry"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.reGenerate"
          })
        }) : /*#__PURE__*/_jsx(AILoadingButton, {
          usageTracker: viewsManagementTracker,
          onClick: handleFormSubmit,
          disabled: loadingGeneratedView,
          loading: loadingGeneratedView,
          "data-test-id": "ai-generate-view-modal-generate-button",
          children: error ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.retry"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.generateButton"
          })
        }), /*#__PURE__*/_jsx(AIButton, {
          use: "tertiary",
          onClick: onClose,
          "data-test-id": "ai-generate-view-modal-cancel-button",
          usageTracker: viewsManagementTracker,
          showBreezeIcon: false,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "viewsManagement.modals.generateView.cancel"
          })
        })]
      })]
    })]
  });
};